import React, { useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { TENDER_ACTIVITY_LOGS } from '../../../api/gql/query'
import { Badge } from '@/components/ui/badge'
import { User, Building2, FileText, Gavel, Clock, Send, ArrowRightLeft, Bell, Paperclip, MessageSquare } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TenderActivityLogProps {
  tenderId?: string
}

const EVENT_MAP: Record<string, { icon: any; color: string; label: string }> = {
  BID_SENT: { icon: Send, color: 'bg-green-500', label: 'Bid submitted' },
  TENDER_24H_NOTIFICATION_SENT: { icon: Bell, color: 'bg-amber-500', label: 'Deadline reminder sent' },
}

const getEventMeta = (title: string) => {
  // Check exact match for Log messages
  if (EVENT_MAP[title]) return EVENT_MAP[title]

  // State change messages from Log
  if (title?.includes('state changed')) {
    return { icon: ArrowRightLeft, color: 'bg-blue-500', label: title }
  }

  // Heuristic matching for ActivityLog titles
  const lower = (title || '').toLowerCase()
  if (lower.includes('file') || lower.includes('attach')) return { icon: Paperclip, color: 'bg-violet-500', label: title }
  if (lower.includes('bid') || lower.includes('nabíd')) return { icon: Gavel, color: 'bg-green-500', label: title }
  if (lower.includes('bidder') || lower.includes('uchazeč')) return { icon: Building2, color: 'bg-cyan-500', label: title }
  if (lower.includes('creat') || lower.includes('add') || lower.includes('nový')) return { icon: Clock, color: 'bg-green-500', label: title }
  if (lower.includes('delet') || lower.includes('remov') || lower.includes('smazán')) return { icon: Clock, color: 'bg-red-500', label: title }
  if (lower.includes('sent') || lower.includes('notification')) return { icon: Send, color: 'bg-amber-500', label: title }

  return { icon: Clock, color: 'bg-gray-400', label: title }
}

type FilterType = 'MESSAGE' | 'STATE_CHANGE' | 'EVENT' | 'ACTIVITY'

const FILTER_OPTIONS: { key: FilterType; icon: any; color: string; labelKey: string }[] = [
  { key: 'MESSAGE', icon: MessageSquare, color: 'bg-indigo-500', labelKey: 'Tender_activity_filter_messages' },
  { key: 'STATE_CHANGE', icon: ArrowRightLeft, color: 'bg-blue-500', labelKey: 'Tender_activity_filter_status_changes' },
  { key: 'EVENT', icon: Bell, color: 'bg-amber-500', labelKey: 'Tender_activity_filter_events' },
  { key: 'ACTIVITY', icon: Clock, color: 'bg-gray-500', labelKey: 'Tender_activity_filter_activity' },
]

const getLogFilterType = (log: any): FilterType => {
  if (log.type === 'MESSAGE') return 'MESSAGE'
  if (log.type === 'STATE_CHANGE') return 'STATE_CHANGE'
  if (log.type === 'EVENT') return 'EVENT'
  return 'ACTIVITY'
}

export const TenderActivityLog = ({ tenderId }: TenderActivityLogProps) => {
  const { t } = useTranslation()
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set())
  const { data, loading } = useQuery(TENDER_ACTIVITY_LOGS, { variables: { tenderId } })
  const activityLogs = data?.tenderActivityLogs || []

  const toggleFilter = (key: FilterType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  if (loading) {
    return <div className="flex items-center justify-center p-8 text-muted-foreground">{t('loading')}</div>
  }

  if (activityLogs.length === 0) {
    return <div className="flex items-center justify-center p-8 text-muted-foreground">{t('Tender_activity_log_empty')}</div>
  }

  const filteredLogs = activeFilters.size === 0
    ? activityLogs
    : activityLogs.filter((log: any) => activeFilters.has(getLogFilterType(log)))

  // Group logs by date
  const grouped: Record<string, any[]> = {}
  for (const log of filteredLogs) {
    const dateKey = moment(log.createdAt).format('YYYY-MM-DD')
    if (!grouped[dateKey]) grouped[dateKey] = []
    grouped[dateKey].push(log)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {FILTER_OPTIONS.map(({ key, icon: Icon, color, labelKey }) => {
          const isActive = activeFilters.has(key)
          return (
            <button
              key={key}
              onClick={() => toggleFilter(key)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors border',
                isActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted',
              )}
            >
              <Icon className="h-3 w-3" />
              {t(labelKey)}
            </button>
          )
        })}
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="flex items-center justify-center p-8 text-muted-foreground">{t('Tender_activity_log_empty')}</div>
      )}

      {Object.entries(grouped).map(([dateKey, logs]) => (
        <div key={dateKey}>
          <div className="sticky top-0 z-[1] bg-background pb-2">
            <Badge variant="outline" className="text-xs font-normal">
              {moment(dateKey).calendar(null, {
                sameDay: `[${t('today')}]`,
                lastDay: `[${t('yesterday')}]`,
                lastWeek: 'dddd',
                sameElse: 'LL',
              })}
            </Badge>
          </div>

          <div className="relative ml-3 border-l border-border pl-6 space-y-4">
            {logs.map((log: any, idx: number) => {
              const isMessage = log.type === 'MESSAGE'
              const meta = isMessage
                ? { icon: MessageSquare, color: 'bg-indigo-500', label: log.title }
                : getEventMeta(log.title)
              const IconComponent = meta.icon
              const actor = log.user?.name || log.bidder?.name

              return (
                <div key={idx} className="relative">
                  <div className={`absolute -left-[31px] top-1 h-4 w-4 rounded-full ${meta.color} flex items-center justify-center`}>
                    <IconComponent className="h-2.5 w-2.5 text-white" />
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {isMessage ? (
                        <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">{meta.label}</div>
                      ) : (
                        <p className="text-sm font-medium">{meta.label}</p>
                      )}
                      {!isMessage && log.description && (
                        <p className="text-sm text-muted-foreground mt-0.5">{log.description}</p>
                      )}
                      {actor && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{actor}</span>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                      {moment(log.createdAt).format('HH:mm')}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default TenderActivityLog
